// The five setup steps (Paper 10a to 10e) and the roles the first hire picks from.
// The step lives in the URL (/setup?step=model) so back and forward walk the flow.

export const setupSteps = [
  { id: "org", label: "Org" },
  { id: "model", label: "Model" },
  { id: "hire", label: "First hire" },
  { id: "people", label: "People" },
  { id: "ready", label: "Ready" },
] as const;

export type SetupStep = (typeof setupSteps)[number]["id"];

export const isSetupStep = (value: unknown): value is SetupStep =>
  setupSteps.some((step) => step.id === value);

export const stepIndex = (step: SetupStep) => setupSteps.findIndex((candidate) => candidate.id === step);

export const nextStep = (step: SetupStep): SetupStep => setupSteps[stepIndex(step) + 1]?.id ?? "ready";
export const previousStep = (step: SetupStep): SetupStep => setupSteps[stepIndex(step) - 1]?.id ?? "org";

export interface RoleTemplate {
  readonly id: string;
  readonly title: string;
  readonly caption: string;
  readonly name: string;
  readonly hue: "amber" | "blue" | "violet" | "teal";
  readonly face: "glasses" | "shades" | "brow" | "plain";
  readonly persona: string;
}

export const roleTemplates: ReadonlyArray<RoleTemplate> = [
  {
    id: "expenses",
    title: "Expense manager",
    caption: "Files receipts and closes the month",
    name: "Ledger",
    hue: "amber",
    face: "glasses",
    persona:
      "Files receipts from email, keeps the monthly ledger and sends accounting one report a month. Precise and brief, and asks before emailing anyone outside the team.",
  },
  {
    id: "inbox",
    title: "Inbox manager",
    caption: "Triages mail and drafts replies",
    name: "Inbox",
    hue: "blue",
    face: "shades",
    persona:
      "Triages the inbox every morning: archives the noise, flags what needs a reply and drafts the easy ones. Never sends without a yes.",
  },
  {
    id: "talent",
    title: "Talent scout",
    caption: "Finds candidates and drafts outreach",
    name: "Scout",
    hue: "violet",
    face: "brow",
    persona:
      "Finds and shortlists candidates for open roles, reads portfolios and drafts outreach in your voice. Never contacts a candidate without a yes.",
  },
  {
    id: "research",
    title: "Research assistant",
    caption: "Digs through the web and summarises",
    name: "Atlas",
    hue: "teal",
    face: "plain",
    persona: "Digs through the web, reads the sources that matter and writes short summaries with links.",
  },
];

/** "Ledger" -> "ledger", "Mr. Books 2" -> "mr-books-2". Handles are what @mentions match. */
export const handleFor = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^[^a-z]+|-+$/g, "");
